import React from 'react';
import {useAppSelector} from '../../../utils/Hooks';
import {API_URL} from '../../../utils/Constants';
import {RequestStatus} from '../../../utils/Enums';
import {InvoiceHistoryMetadata} from '../../../models/InvoiceHistory';

const formatDateTime = (value: string | null | undefined): string => {
    if (!value) return '–';

    return new Intl.DateTimeFormat('et-EE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(value));
};

const formatDate = (value: string | null | undefined): string => {
    if (!value) return '–';

    const [year, month, day] = value.split('-');
    return year && month && day ? `${day}.${month}.${year}` : value;
};

const yesNo = (value: boolean): string => value ? 'jah' : 'ei';

const Metadata: React.FC<{metadata: InvoiceHistoryMetadata | null}> = ({metadata}) => {
    if (!metadata) return <span>–</span>;

    return (
        <details>
            <summary style={{cursor: 'pointer'}}>Vaata metaandmeid</summary>
            <div style={{minWidth: '270px', lineHeight: 1.5, paddingTop: '6px'}}>
                <strong>Tellimus:</strong> #{metadata.source_order_id}<br/>
                <strong>Tellimuse kuupäev:</strong> {formatDate(metadata.order_date)}<br/>
                <strong>Laadimiskuupäev:</strong> {formatDate(metadata.loading_date)}<br/>
                <strong>Piiraeg:</strong> {formatDateTime(metadata.cutoff_at)}
                {metadata.cutoff_hours ? ` (${metadata.cutoff_hours} tundi)` : ''}<br/>
                <strong>Kontrollimise aeg:</strong> {formatDateTime(metadata.evaluated_at)}<br/>
                <strong>Ettemaks:</strong> {yesNo(metadata.client.prepayment)}<br/>
                <strong>Finantseerimine:</strong> {yesNo(metadata.client.finance)}<br/>
                <strong>Maksetähtaeg:</strong> {metadata.client.payment_deadline_days} päeva<br/>
                <strong>Arve lisamisel:</strong> {yesNo(metadata.service.invoice_on_add)}<br/>
                <strong>Automaatne arveldus keelatud:</strong> {yesNo(metadata.service.automatic_invoicing_disabled)}
                {metadata.previous_invoice_number && <><br/><strong>Varasem arve:</strong> {metadata.previous_invoice_number}</>}
                {metadata.required_insurance_service_ids.length > 0 && (
                    <><br/><strong>Nõutud kindlustusteenused:</strong> {metadata.required_insurance_service_ids.join(', ')}</>
                )}
                {metadata.included_rows.length > 0 && (
                    <>
                        <br/><strong>Arve read:</strong>
                        <ul style={{margin: '2px 0 0 18px', padding: 0}}>
                            {metadata.included_rows.map((row) => (
                                <li key={row.order_id}>
                                    #{row.order_id}: {row.service_name || 'Teenus'} ({row.service_id}), lisatud {formatDateTime(row.created_at)}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </details>
    );
};

export const InvoiceHistoryTable: React.FC = () => {
    const invoiceHistory = useAppSelector((state) => state.invoiceHistory);
    const token = localStorage.getItem('token') || '';

    if (invoiceHistory.status === RequestStatus.Loading) {
        return <p>Arvete ajaloo laadimine…</p>;
    }

    if (invoiceHistory.status === RequestStatus.Failed) {
        return <p className="error">{invoiceHistory.error}</p>;
    }

    return (
        <table className="data" style={{width: '100%'}}>
            <thead>
            <tr>
                <th style={{width: '8%'}}>Arve nr</th>
                <th style={{width: '13%'}}>Klient</th>
                <th style={{width: '10%'}}>Saatja</th>
                <th style={{width: '10%'}}>Saatmise aeg</th>
                <th style={{width: '36%'}}>Saatmise põhjus</th>
                <th style={{width: '23%'}}>Metaandmed</th>
            </tr>
            </thead>
            <tbody>
            {invoiceHistory.entries.length === 0 && (
                <tr><td colSpan={6}>Arveid ei leitud.</td></tr>
            )}
            {invoiceHistory.entries.map((entry) => (
                <tr key={entry.id}>
                    <td>
                        <a
                            href={`${API_URL}invoice-history/${entry.id}/download?token=${encodeURIComponent(token)}`}
                            target="_blank"
                            rel="noreferrer"
                            title="Laadi arve PDF alla"
                        >
                            {entry.invoice_number}
                        </a>
                    </td>
                    <td>{entry.client.name}</td>
                    <td
                        style={entry.attribution_error ? {color: '#b00020', fontWeight: 'bold'} : undefined}
                        title={entry.attribution_error ? 'Interaktiivsel arvel puudub kohustuslik kasutaja' : undefined}
                    >
                        {entry.sender.name}
                    </td>
                    <td>{formatDateTime(entry.sent_at)}</td>
                    <td style={{whiteSpace: 'normal', lineHeight: 1.45}}>{entry.reason}</td>
                    <td style={{whiteSpace: 'normal', verticalAlign: 'top'}}><Metadata metadata={entry.metadata}/></td>
                </tr>
            ))}
            </tbody>
        </table>
    );
};
